import type {
  DropDownResult,
  JsonData,
  OdinDataRetrievalOptions,
  OptionWithOptionalProperties,
} from '@myosh/odin-components';
import { isObject } from '@myosh/odin-components';
import { cloneDeep, isEmpty, orderBy } from 'lodash';
import type { AuditLogData, AuditLogDataItem } from '../@types/audit';
import type { ModuleForm, ModuleForms, Modules } from '../@types/modules';
import type { NewsInfo, NewsResult } from '../@types/news';
import type {
  DesirableCompetencies,
  HierarchyFields,
  MandatoryCompetencies,
  OccupationalRequirementsItem,
} from '../@types/occupational-requirements';
import type { RecordResult, RecordStructure } from '../@types/records';
import type { AddonsValidation, ViewConfigsWrapper } from '../@types/views';
import type { NewsEntryProps } from '../components/home-page/news-entry.component';
import i18next from '../i18n';
import { forceAssert } from './type-guards';

function getTopLevelProperties(object: Record<string, unknown>) {
  const properties: Record<string, unknown> = {};
  const keys = Object.keys(object);

  for (let i = 0, length = keys.length; i < length; i++) {
    if (Object.hasOwn(object, keys[i])) {
      properties[keys[i]] = object[keys[i]];
    }
  }

  return properties;
}

export function toDropDownResult(originalField: unknown): DropDownResult | OptionWithOptionalProperties {
  let result: DropDownResult | OptionWithOptionalProperties = forceAssert<
    DropDownResult | OptionWithOptionalProperties
  >(originalField);
  if (
    isObject(originalField) &&
    Object.hasOwn(originalField, 'id') &&
    Object.hasOwn(originalField, 'value') &&
    Object.hasOwn(originalField, 'position') &&
    Object.hasOwn(originalField, 'archivedOption') &&
    Object.hasOwn(originalField, 'defaultOption')
  ) {
    result = {
      value: originalField.id,
      text: originalField.value as string,
      originalData: originalField,
      position: originalField.position as number,
      archivedOption: originalField.archivedOption as boolean,
      defaultOption: originalField.defaultOption as boolean,
    };
  } else if (isObject(originalField) && Object.hasOwn(originalField, 'id') && Object.hasOwn(originalField, 'value')) {
    result = {
      value: originalField.id,
      text: originalField.value as string,
      originalData: originalField,
    };
  } else if (isObject(originalField) && Object.hasOwn(originalField, 'id') && Object.hasOwn(originalField, 'name')) {
    // special format for TRAINING field types
    result = {
      value: originalField.id,
      text: originalField.name as string,
      originalData: originalField,
    };
  }
  return result;
}

export function createRecordData(item: RecordResult) {
  const dataItem: RecordStructure = {
    original: {
      ...cloneDeep(item),
    },
    flat: {},
  };

  const clonedItem = cloneDeep(item);
  const fieldKeys = Object.keys(clonedItem.fields);
  for (let i = 0, length = fieldKeys.length; i < length; i++) {
    const originalField = clonedItem.fields[fieldKeys[i]];
    if (typeof originalField === 'object') {
      if (Array.isArray(originalField)) {
        for (let j = 0; j < originalField.length; j++) {
          forceAssert<Array<unknown>>(clonedItem.fields[fieldKeys[i]])[j] = toDropDownResult(originalField[j]);
        }
      } else {
        clonedItem.fields[fieldKeys[i]] = toDropDownResult(originalField);
      }
    }
  }

  dataItem.flat = {
    ...clonedItem.fields,
    ...getTopLevelProperties(forceAssert<Record<string, unknown>>(clonedItem)),
  };

  return dataItem;
}

/**
 * Transforms the payload received when fetching news into props that
 * NewsEntry component can accept.
 *
 * @param response The payload received from the GET news endpoint.
 * @returns An array of NewsEntry props that can be passed to said component.
 */
export const transformNewsEntries = (response: NewsResult) => {
  const news = response.result || [];

  const parsedNews: Array<NewsEntryProps> = news.map((newsEntry: NewsInfo) => {
    return {
      ...newsEntry,
      date: newsEntry.published,
      author: newsEntry.author?.value,
    };
  });

  return parsedNews;
};

function transformSingleRequirementsData(occupationalRequirementsResponse: OccupationalRequirementsItem) {
  const _mandatoryCompetencies: Array<MandatoryCompetencies> = [];
  const _desirableCompetencies: Array<DesirableCompetencies> = [];
  const _hierarchyFields: Array<HierarchyFields> = [];
  const {
    mandatoryCompetencies = [],
    desirableCompetencies = [],
    hierarchyFields,
    hierarchyMatchingOption: matchOption,
  } = occupationalRequirementsResponse;

  for (let j = 0; j < mandatoryCompetencies.length; j++) {
    const mandatoryCompetency = mandatoryCompetencies[j];

    _mandatoryCompetencies.push({
      ...mandatoryCompetency,
      text: mandatoryCompetency.name,
      value: mandatoryCompetency.id,
    });
  }

  for (let j = 0; j < desirableCompetencies.length; j++) {
    const desirableCompetency = desirableCompetencies[j];

    _desirableCompetencies.push({
      ...desirableCompetency,
      text: desirableCompetency.name,
      value: desirableCompetency.id,
    });
  }

  const _hierarchyPerTypeHelper: Record<string, Array<string>> = {};
  for (let j = 0; j < hierarchyFields.length; j++) {
    const hierarchyField = hierarchyFields[j];
    const translationItem = hierarchyField.caption?.translations[0];

    _hierarchyFields.push({
      ...translationItem,
      text: translationItem?.value,
      value: translationItem?.value,
    });

    const hierachyKey = hierarchyField.hierarchyTypeCaption?.translations[0]?.value;
    if (hierachyKey && translationItem?.value) {
      const currentValues = _hierarchyPerTypeHelper[hierachyKey] ?? [];
      _hierarchyPerTypeHelper[hierachyKey] = [...currentValues, translationItem.value];
    }
  }

  const _hierarchyMatchingOption = matchOption ? { text: matchOption, value: matchOption } : undefined;

  const _hierarchyPerType: Record<string, string> = {};
  Object.keys(_hierarchyPerTypeHelper).forEach((key) => {
    _hierarchyPerType[key] = _hierarchyPerTypeHelper[key].join(', ');
  });
  return {
    ...occupationalRequirementsResponse,
    id: occupationalRequirementsResponse.id,
    mandatoryCompetencies: _mandatoryCompetencies,
    desirableCompetencies: _desirableCompetencies,
    hierarchyFields: _hierarchyFields,
    hierarchyMatchingOption: _hierarchyMatchingOption,
    ..._hierarchyPerType,
  };
}

export function transformOccupationRequirements(items: Array<OccupationalRequirementsItem>) {
  const clonedItems = cloneDeep(items);

  const _items: Array<OccupationalRequirementsItem> = [];
  for (let i = 0; i < clonedItems.length; i++) {
    _items.push(transformSingleRequirementsData(clonedItems[i]));
  }

  return _items;
}

export const allFormsForm: ModuleForm = {
  caption: i18next.t('all-forms'),
  defaultForm: false,
  displayInForms: true,
  id: 0,
  removed: false,
  roles: [],
  workflowId: 0,
};

export function transformModuleForms(response: ModuleForms) {
  const formsOrEmpty = response?.result?.forms ?? [];

  const sortedForms: ModuleForms = {
    ...formsOrEmpty,
    result: {
      ...response.result,
      forms: orderBy(formsOrEmpty, [(form) => form.caption.toLowerCase()], ['asc']),
    },
  };

  if (!isEmpty(sortedForms.result.forms)) {
    sortedForms.result.forms?.push(allFormsForm);
  }

  return sortedForms;
}

export function transformModules(modules: Modules) {
  return modules.items
    ? modules.items.map((module) => {
        return {
          ...module,
          forms: orderBy([...(module.forms ?? [])], [(form) => form.caption.toLowerCase()], ['asc']),
        };
      })
    : [];
}

export function transformAuditLogResponse(auditData: AuditLogData[]) {
  return auditData.map((auditItem: AuditLogData, index: number) => {
    return {
      ...auditItem,
      id: index,
      recordVersionUpdate:
        typeof auditItem.recordVersionUpdate === 'boolean' ? String(auditItem.recordVersionUpdate) : '',
    };
  });
}

export function transformAuditLogDetailsResponse(auditData: AuditLogDataItem[], version: string) {
  return auditData.map((auditItem) => {
    return {
      ...auditItem,
      version: version,
    };
  });
}

export function transformFormViewConfigrations(response: ViewConfigsWrapper) {
  const result = response?.result ?? [];
  return [
    ...result.filter((element) => element.defaultConfig),
    ...result.filter((element) => !element.defaultConfig).sort((a, b) => a.name?.localeCompare(b.name)),
  ];
}

export function transformFormAddons(response?: Array<AddonsValidation>) {
  if (response) {
    const listOfAddons: Array<AddonsValidation> = [];
    for (let i = 0, addonsListLength = response?.length; i < addonsListLength; i++) {
      if (response[i].success === true) {
        listOfAddons.push(response[i]);
      }
    }
    return listOfAddons;
  }
}

type FormattingFunction = (filterKey: string, originalValue: unknown) => unknown;

export const dateFormatBasedOnFilter: FormattingFunction = (filterKey, originalValue) => {
  if (originalValue instanceof Date) {
    if (filterKey !== 'creationDate' && filterKey !== 'lastChange') {
      return originalValue.toISOString().replace('Z', '');
    }
  }
};

export const filterDropdownOptionsByText = (items: JsonData, options: OdinDataRetrievalOptions): JsonData => {
  const filterText = options?.fieldFilters?.text?.value as string | undefined;
  return filterText
    ? items.filter(({ text }) => (text as string).toLowerCase().includes(filterText.toLowerCase()))
    : items;
};

/**
 * Capitalize the first letter of a word
 * @param word the word to capitalize
 * @returns capitalized word
 */
export const capitalizeFirstLetter = (word: string) => {
  if (word.length > 0) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  return word;
};

/**
 * Convert a blob to a source URL
 * @param blob the blob to convert
 * @returns the source URL
 */
export function fileToSrc(blob: Blob) {
  return window.URL.createObjectURL(blob);
}
